import { Injectable } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { ErrorResponse, GlobalResponseObject } from '@shahi-analytics/backend-utils';
import axios from 'axios';
import { In, Repository } from 'typeorm';
import configuration from '../../config/configuration';
import { LineEntity } from '../entities/line.entity';
import { OperationEntity } from '../entities/operation.entity';
import { OperatorEntity } from '../entities/operator.entity';
import { ReasonEntity } from '../entities/reason.entity';
import { StyleOperationsEntity } from '../entities/style-operations.entity';
import { StyleEntity } from '../entities/style.entity';
import { LineActualDayTrackEntity } from '../entities/transactions/line-actual-track.entity';
import { LineDownTimeEntity } from '../entities/transactions/line-down-time.entity';
import { LineEfficiencyDayTrackEntity } from '../entities/transactions/line-efficiency.entity';
import { LineDayPlanEntity } from '../entities/transactions/line-plan';
import { MigrationLogEntity } from '../entities/transactions/migration-log.entity';
import { OperatorEfficiencyDayTrackEntity } from '../entities/transactions/operator-efficiency-track.entity';
import { WorkstationEntity } from '../entities/workstation.entity';
import { DbNamesEnum } from './db-connection/db-names.enum';
import { ShahiDbConnectionsService } from './db-connection/shahi-db-connection';
import { AppTableNamesEnum } from './models/app-table-names.enum';
import { MigrationStatusEnum } from './models/migration-status.enum';
import { ReasonPriorityEnum } from './models/reason-priority-enum';
import { UnitDateHourRequest } from './models/unit-date-hour.request';
import { UnitBreakEntity } from '../entities/unit-break.entity';
import { UnitDayBreakEntity } from '../entities/unit-day-break.entity';
import { UnitDayHourEntity } from '../entities/unit-day-hours.entity';
import { MigrationHelperService } from './migration-helper.service';
import { TempUnitModel } from './models/plan/temp-unit.model';
import { TempLineModel } from './models/plan/temp-line.model';
import { UnitEntity } from '../entities/unit.entity';
import { GenericTransactionManager } from '../../database/typeorm-transactions';
import { LineTargetEffRequest } from './models/line-target-eff.request';
import { LineTargetEffEntity } from '../entities/line-target-eff.entity';
import { StyleModel, StyleTargetEffRequest } from '@shahi-analytics/shared-models';

@Injectable()
export class MigrationService {
    private dbName = 'dbo';

    constructor(
        @InjectRepository(UnitDayHourEntity) private unitDayHoursEnt: Repository<UnitDayHourEntity>,
        @InjectRepository(UnitBreakEntity) private unitBreakEnt: Repository<UnitBreakEntity>,
        @InjectRepository(UnitDayBreakEntity) private unitDayBreakEnt: Repository<UnitDayBreakEntity>,
        @InjectRepository(LineActualDayTrackEntity) private lineActualDayTrackEnt: Repository<LineActualDayTrackEntity>,
        @InjectRepository(StyleEntity) private styleEnt: Repository<StyleEntity>,
        @InjectRepository(UnitEntity) private unitEnt: Repository<UnitEntity>,
        @InjectRepository(LineEntity) private lineEnt: Repository<LineEntity>,
        @InjectRepository(LineDayPlanEntity) private linePlanEnt: Repository<LineDayPlanEntity>,
        @InjectRepository(StyleOperationsEntity) private styleOpEnt: Repository<StyleOperationsEntity>,
        @InjectRepository(OperationEntity) private operationEnt: Repository<OperationEntity>,
        @InjectRepository(OperatorEfficiencyDayTrackEntity) private empEffEnt: Repository<OperatorEfficiencyDayTrackEntity>,
        @InjectRepository(WorkstationEntity) private workEnt: Repository<WorkstationEntity>,
        @InjectRepository(LineEfficiencyDayTrackEntity) private lineEffEnt: Repository<LineEfficiencyDayTrackEntity>,
        @InjectRepository(OperatorEntity) private operatorEnt: Repository<OperatorEntity>,
        @InjectRepository(MigrationLogEntity) private migLogEnt: Repository<MigrationLogEntity>,
        @InjectRepository(ReasonEntity) private reasonEnt: Repository<ReasonEntity>,
        @InjectRepository(LineDownTimeEntity) private lineDownEnt: Repository<LineDownTimeEntity>,
        @InjectRepository(LineTargetEffEntity) private lineTargetEffEnt: Repository<LineTargetEffEntity>,
        private migHelperService: MigrationHelperService,
        private shahiDb: ShahiDbConnectionsService
    ) {
        
    }

    //Not required
    async createLines(req: UnitDateHourRequest): Promise<GlobalResponseObject> {
        try{
            const inaDbConn = await this.shahiDb.getShahiDbConnectionForLineAndUnit(null, req.unit, req.lineId);
            // get the records from the current table
            const alreadyMigrated = await this.migHelperService.checkIfTableAlreadyMigratedForDayHour(req.lineId,req.unit, AppTableNamesEnum.M_LINE, req.date, req.hour);
            if(alreadyMigrated) {
                throw new ErrorResponse(0, AppTableNamesEnum.M_LINE + 'Table already migrated for the date + hour ' + req.date + ' : ' + req.hour);
            }
            // get all the existing lines in the local db
            const lines = await this.lineEnt.find({ select: ['lineId', 'lineNumber'], where: {unitId: req.unit}});
            const lineInfoMap = new Map<string, string>();
            lines.forEach(rec => {
                lineInfoMap.set(rec.lineId, rec.lineNumber);
            });

            // create a migration log record for the current table and the hour
            // await this.createTableMigratingLog(req.unit, AppTableNamesEnum.M_LINE, req.date, req.hour);
            // move the status to completed
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_LINE, req.date, req.hour, MigrationStatusEnum.PROGRESS);
            // get all the lines from the SHAHI INA
            // const query = "SELECT Line_Number,Line_Description,LineId FROM "+this.dbName+".B_Line_information WHERE GroupId = "+req.unit;
            const query = `SELECT LineId, LineName FROM dbo.T_DbInfo where DbName = 'IHS' AND GroupId = ${req.unit}`;
            const result: {LineName: string, LineId: number}[] = await inaDbConn.query(query);

            
            const newLines: LineEntity[] = [];
            for(const rec of result) {
                const lineId = rec.LineId;
                if(lineInfoMap.has(lineId.toString())) {
                    // if already the line is there in the local db, then skip it
                } else {
                    const newLineEnt = new LineEntity();

                    newLineEnt.lineId = rec.LineId.toString();
                    newLineEnt.lineDesc = rec.LineName;
                    newLineEnt.unitId = req.unit;
                    newLineEnt.lineNumber = rec.LineId.toString();
                    newLines.push(newLineEnt);
                }
            }
            // const transManager = new GenericTransactionManager();
            // await transManager.startTransaction();
            // await transManager.getRepository(LineEntity).save(newLines, {reload: false});
            // await transManager.completeTransaction();

            await this.lineEnt.save(newLines, {reload: false});
            // move the status to completed
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_LINE, req.date, req.hour, MigrationStatusEnum.COMPLETED);

            return new GlobalResponseObject(true, 0, 'Lines data dumped');
        } catch (error) {
            console.log(error);
            // move the status to completed
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_LINE, req.date, req.hour, MigrationStatusEnum.FAILED, error.message);
            return new GlobalResponseObject(false, 0, error.message);
        }
    }

    // TESTED
    // currently no workstations mapped to all the lines
    async createWorkstations(req: UnitDateHourRequest): Promise<GlobalResponseObject> {
        try {
            const inaDbConn = await this.shahiDb.getShahiDbConnectionForLineAndUnit(DbNamesEnum.IHS,  req.unit, req.lineId);

            // get the records from the current table
            const alreadyMigrated = await this.migHelperService.checkIfTableAlreadyMigratedForDayHour(req.lineId,req.unit, AppTableNamesEnum.M_WORKSTATION, req.date, req.hour);
            if(alreadyMigrated) {
                throw new ErrorResponse(0, AppTableNamesEnum.M_WORKSTATION + ' : Table already migrated for the date + hour ' + req.date + ' : ' + req.hour);
            }

            // get all the existing lines in the local db
            const workstations = await this.workEnt.find({ select: ['lineId', 'workstationId'], where: {unitId: req.unit}});
            const lineWorkstationsMap = new Map<string, Set<string>>();
            workstations.forEach(rec => {
                if(!lineWorkstationsMap.has(rec.lineId)) {
                    lineWorkstationsMap.set(rec.lineId, new Set<string>());
                }
                lineWorkstationsMap.get(rec.lineId).add(rec.workstationId);
            });

            // create a migration log record for the current table and the hour
            // await this.createTableMigratingLog(req.unit, AppTableNamesEnum.M_WORKSTATION, req.date, req.hour);
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_WORKSTATION, req.date, req.hour, MigrationStatusEnum.PROGRESS);

            // get all the lines from the SHAHI INA
            const query = `SELECT Workstation, ${req.lineId} as LineId FROM ${this.dbName}.Line_Workstation_Setup`;
            const result: {Workstation: number,	LineId: string}[] = await inaDbConn.query(query);  
            
            const newWorks: WorkstationEntity[] = [];
            for(const rec of result) {
                const lineId = rec.LineId?.toString();
                const w = rec.Workstation?.toString();
                if(lineWorkstationsMap.get(lineId)?.has(w)) {
                    // if already the line is there in the local db, then skip it
                } else {
                    const newLineEnt = new WorkstationEntity();

                    newLineEnt.lineId = rec.LineId?.toString();
                    newLineEnt.workstationId = rec.Workstation?.toString();
                    newLineEnt.workstationDesc = rec.Workstation?.toString();
                    newLineEnt.unitId = req.unit;
                    
                    newWorks.push(newLineEnt);
                }
            }
            await this.workEnt.save(newWorks, {reload: false});
            // move the status to completed
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_WORKSTATION, req.date, req.hour, MigrationStatusEnum.COMPLETED);
            return new GlobalResponseObject(true, 0, 'Workstations data dumped');
        } catch (error) {
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_WORKSTATION, req.date, req.hour, MigrationStatusEnum.FAILED, error.message);
            return new GlobalResponseObject(false, 0, error.message);
        }
    }

    // TESTED
    // note: when getting an operator, the line id is mandatory
    async createOperators(req: UnitDateHourRequest): Promise<GlobalResponseObject> {
        try {
            const inaDbConn = await this.shahiDb.getShahiDbConnectionForLineAndUnit(DbNamesEnum.IHS_SHARED,  req.unit, req.lineId);
            // get the records from the current table
            const alreadyMigrated = await this.migHelperService.checkIfTableAlreadyMigratedForDayHour(req.lineId,req.unit, AppTableNamesEnum.M_OPERATOR, req.date, req.hour);
            if(alreadyMigrated) {
                throw new ErrorResponse(0, AppTableNamesEnum.M_OPERATOR + ' : Table already migrated for the date + hour ' + req.date + ' : ' + req.hour);
            }
            // get all the existing lines in the local db
            const operators = await this.operatorEnt.find({ select: ['lineId', 'operatorId', 'operatorRfId'], where: {unitId: req.unit}});
            const lineOperatorsMap = new Map<string, Set<string>>();
            operators.forEach(rec => {
                if(!lineOperatorsMap.has(rec.lineId)) {
                    lineOperatorsMap.set(rec.lineId, new Set<string>());
                }
                lineOperatorsMap.get(rec.lineId).add(rec.operatorRfId);
            });

            // create a migration log record for the current table and the hour
            // await this.createTableMigratingLog(req.unit, AppTableNamesEnum.M_OPERATOR, req.date, req.hour);
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_OPERATOR, req.date, req.hour, MigrationStatusEnum.PROGRESS);

            // get all the lines from the SHAHI INA
            const query = `SELECT EM_Key, EM_RFID, EM_FirstName, ${req.lineId} as LineId
            FROM ${this.dbName}.Employee_Master`;
            const result: {EM_Key: number,EM_RFID: string,EM_FirstName: string,LineId: number }[] = await inaDbConn.query(query);  
            
            const newOperators: OperatorEntity[] = [];
            for(const rec of result) {
                const lineId = rec.LineId?.toString();
                const emRfId = rec.EM_RFID;
                const emKey = rec.EM_Key?.toString();
                if(lineOperatorsMap.get(lineId)?.has(emRfId)) {
                    // if already the line is there in the local db, then skip it
                } else {
                    const newOperatorEnt = new OperatorEntity();
                
                    newOperatorEnt.operatorId = emKey;
                    newOperatorEnt.operatorName = rec.EM_FirstName;
                    newOperatorEnt.operatorRfId = emRfId;
                    newOperatorEnt.lineId = rec.LineId?.toString();
                    newOperatorEnt.unitId = req.unit;
                    
                    newOperators.push(newOperatorEnt);
                }
            }

            await this.operatorEnt.save(newOperators, {reload: false});
            // move the status to completed
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_OPERATOR, req.date, req.hour, MigrationStatusEnum.COMPLETED);

            return new GlobalResponseObject(true, 0, 'Operators data dumped');
        } catch (error) {
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_OPERATOR, req.date, req.hour, MigrationStatusEnum.FAILED, error.message);
            return new GlobalResponseObject(false, 0, error.message);
        }
    }

    // TESTED
    // note: when getting an operation, the line id is mandatory
    async createOperations(req: UnitDateHourRequest): Promise<GlobalResponseObject> {
        try {
            const inaDbConn = await this.shahiDb.getShahiDbConnectionForLineAndUnit(DbNamesEnum.IHS_SHARED,  req.unit, req.lineId);
            // get the records from the current table
            const alreadyMigrated = await this.migHelperService.checkIfTableAlreadyMigratedForDayHour(req.lineId,req.unit, AppTableNamesEnum.M_OPERATION, req.date, req.hour);
            if(alreadyMigrated) {
                throw new ErrorResponse(0, AppTableNamesEnum.M_OPERATION + ' : Table already migrated for the date + hour ' + req.date + ' : ' + req.hour);
            }
            // get all the existing lines in the local db
            const operators = await this.operationEnt.find({ select: ['lineId', 'operationId'], where: {unitId: req.unit}});
            const lineOperationsMap = new Map<string, Set<string>>();
            operators.forEach(rec => {
                if(!lineOperationsMap.has(rec.lineId)) {
                    lineOperationsMap.set(rec.lineId, new Set<string>());
                }
                lineOperationsMap.get(rec.lineId).add(rec.operationId);
            });

            // create a migration log record for the current table and the hour
            // await this.createTableMigratingLog(req.unit, AppTableNamesEnum.M_OPERATION, req.date, req.hour);
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_OPERATION, req.date, req.hour, MigrationStatusEnum.PROGRESS);

            // get all the lines from the SHAHI INA
            const query = `SELECT OC_Key,OC_Sub_Code,OC_Description,OC_PC_Key, ${req.lineId} as LineId
            FROM ${this.dbName}.Operation_Codes`;
            const result: {OC_Key: number,OC_Sub_Code: string,OC_Description: string,OC_PC_Key:number, LineId: number }[] = await inaDbConn.query(query);  
            
            const newOperations: OperationEntity[] = [];
            for(const rec of result) {
                const lineId = rec.LineId?.toString();
                const opId = rec.OC_Key?.toString();
                const opSubCode = rec.OC_Sub_Code;
                const opDesc = rec.OC_Description;
                const pcKey = rec.OC_PC_Key?.toString();
                if(lineOperationsMap.get(lineId)?.has(opId)) {
                    // if already the line is there in the local db, then skip it
                } else {
                    const newOperationEnt = new OperationEntity();
                
                    newOperationEnt.operationId = opId;
                    newOperationEnt.operationCode = opSubCode;
                    newOperationEnt.operationDesc = opDesc;
                    newOperationEnt.pcKey = pcKey;
                    newOperationEnt.lineId = rec.LineId?.toString();
                    newOperationEnt.unitId = req.unit;
                    
                    newOperations.push(newOperationEnt);
                }
            }
            await this.operationEnt.save(newOperations, {reload: false});
            // move the status to completed
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_OPERATION, req.date, req.hour, MigrationStatusEnum.COMPLETED);
            return new GlobalResponseObject(true, 0, 'Operations data dumped');
        } catch(error) {
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_OPERATION, req.date, req.hour, MigrationStatusEnum.FAILED, error.message);
            return new GlobalResponseObject(false, 0, error.message);
        }
    }

    // This is a helper end point to create the style and its operations for all the lines in a unit instead of a sinnle line. This is a pre-requisite to be done for past styles data
    async createStylesAndStyleOperationsForAllLinesInUnit(req: UnitDateHourRequest): Promise<GlobalResponseObject> {
        const lines = await this.lineEnt.find({select: ['lineId'], where: {unitId: req.unit, isActive: true}});
        for(const line of lines) {
            // overrdie the line here
            req.lineId = line.lineId;
            await this.createStylesAndStyleOperations(req, true);
        }
        return new GlobalResponseObject(true, 0, 'Styles info for all lines created successfully');
    }

    // TESTED
    // This API runs based on the date. From the given date it will get all the planned styles greater that the current given date
    // note: when getting an operation, the line id is mandatory
    async createStylesAndStyleOperations(req: UnitDateHourRequest, isManualReq: boolean = false): Promise<GlobalResponseObject> {
        try {
            const inaDbConn = await this.shahiDb.getShahiDbConnectionForLineAndUnit(DbNamesEnum.IHS_SHARED,  req.unit, req.lineId);
            
            // get the records from the current table
            const alreadyMigrated = await this.migHelperService.checkIfTableAlreadyMigratedForDayHour(req.lineId,req.unit, AppTableNamesEnum.M_STYLE, req.date, req.hour);
            if(alreadyMigrated && !isManualReq) {
                throw new ErrorResponse(0, AppTableNamesEnum.M_STYLE + ' : Table already migrated for the date + hour ' + req.date + ' : ' + req.hour);
            }

            const styleEnts: StyleEntity[] = [];
            const styleOpEnts: StyleOperationsEntity[] = [];

            // get all the stopm keys from shahi
            const query = `SELECT STOPM_Key, ${req.lineId} as LineId
            FROM ${this.dbName}.Style_Operations_Master WHERE STOPM_Last_Updated >= '${req.date}'`;
            const result: {STOPM_Key: string, LineId: number }[] = await inaDbConn.query(query);

            //if there were any styles planned for the future dates
            if(result.length == 0 && !isManualReq) {
                throw new ErrorResponse(0, 'No new styles planned for the day');
            }
            
            // create a migration log record for the current table and the hour
            // await this.createTableMigratingLog(req.unit, AppTableNamesEnum.M_STYLE, req.date, req.hour);
            // await this.createTableMigratingLog(req.unit, AppTableNamesEnum.M_STYLE_OPERATION, req.date, req.hour);

            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_STYLE, req.date, req.hour, MigrationStatusEnum.PROGRESS);
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_STYLE_OPERATION, req.date, req.hour, MigrationStatusEnum.PROGRESS);

            for(const rec of result) {
                const stopmKey = rec.STOPM_Key;
                const lineId = rec.LineId?.toString();
                // check if we already have this stopm key in our style master
                const styleRec = await this.styleEnt.findOne({ where: { unitId: req.unit, lineId: lineId, stopmKey: stopmKey}});
                if(!styleRec) {
                    // then insert the new record into the style master and also the style operations
                    
                    // get the style info from the style_master
                    const styleInfoQuery = `SELECT ST_Key, ST_ID, ST_Description, ST_Collection FROM ${this.dbName}.Style_Master 
                        WHERE ST_STOPM_Key = '${stopmKey}' `;
                    const styleInfoResult: {ST_Key: number, ST_ID: string, ST_Description: string, ST_Collection: string}[] = await inaDbConn.query(styleInfoQuery);
                    for(const stRec of styleInfoResult) {
                        const styleEnt = new StyleEntity();
                        styleEnt.unitId = req.unit;
                        styleEnt.lineId = lineId;
                        styleEnt.styleId = stRec.ST_Key?.toString();
                        styleEnt.styleName = stRec.ST_ID;
                        styleEnt.styleDesc = stRec.ST_Description;
                        styleEnt.collection = stRec.ST_Collection;
                        styleEnt.stopmKey = stopmKey;

                        styleEnts.push(styleEnt);
                    }

                    
                    if(styleInfoResult?.length > 0) {
                        // get the style operations for the stopm key from the ina
                        const styleOpsQuery = `SELECT stop.STOP_STOPM_Key, stop.STOP_OC_Key, stop.STOP_Order, stop.STOP_Key, 
                            op.OC_Standard_Time, op.OC_Sub_Code, op.OC_PC_Key,
                            sm.ST_Key, sm.ST_ID, sm.ST_Description,	sm.ST_Collection, ${req.lineId} as LineId
                            FROM IHS_SHARED.dbo.Style_Operations stop
                            LEFT JOIN IHS_SHARED.dbo.Operation_Codes op ON stop.STOP_OC_Key = op.OC_Key
                            LEFT JOIN IHS_SHARED.dbo.Style_Master sm ON sm.ST_STOPM_Key = stop.STOP_STOPM_Key
                            WHERE stop.STOP_STOPM_Key = '${stopmKey}'`;
                        const styleOpsResult: {STOP_STOPM_Key: string, LineId: number, STOP_OC_Key: number, STOP_Order: number, STOP_Key:number, OC_Standard_Time: number, OC_Sub_Code: number, OC_PC_Key: number, ST_Key: number, ST_ID: string, ST_Description: string, ST_Collection: string}[] = await inaDbConn.query(styleOpsQuery);


                        for(const sopRec of styleOpsResult) {
                            const styleOpEnt = new StyleOperationsEntity();

                            styleOpEnt.operationId = sopRec.STOP_Key?.toString();
                            styleOpEnt.operationCode = sopRec.OC_Sub_Code?.toString();
                            styleOpEnt.operationOrder = sopRec.STOP_Order;
                            styleOpEnt.operationSmv = sopRec.OC_Standard_Time;
                            styleOpEnt.stopmKey = sopRec.STOP_STOPM_Key;
                            styleOpEnt.lineId = sopRec.LineId?.toString();
                            styleOpEnt.styleId = sopRec.ST_Key?.toString();
                            styleOpEnt.unitId = req.unit;
                            styleOpEnt.styleName = sopRec.ST_Description;

                            styleOpEnts.push(styleOpEnt);
                        }
                    }
                }
            }
            // const transManager = new GenericTransactionManager();
            // await transManager.startTransaction();
            // await transManager.getRepository(StyleEntity).save(styleEnts, {reload: false});
            // await transManager.getRepository(StyleOperationsEntity).save(styleOpEnts, {reload: false})
            // await transManager.completeTransaction();
            await this.styleEnt.save(styleEnts, {reload: false});
            await this.styleOpEnt.save(styleOpEnts, {reload: false});
            // move the status to completed
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_STYLE, req.date, req.hour, MigrationStatusEnum.COMPLETED);
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_STYLE_OPERATION, req.date, req.hour, MigrationStatusEnum.COMPLETED);
            
            return new GlobalResponseObject(true, 0, 'Style and style operations data dumped');
        } catch (error) {
            console.log(error);
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_STYLE, req.date, req.hour, MigrationStatusEnum.FAILED, error.message);
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_STYLE_OPERATION, req.date, req.hour, MigrationStatusEnum.FAILED, error.message);
            return new GlobalResponseObject(false, 0, error.message);
        }
    }

    // TESTED
    // creates the down time reasons
    async createReasons(req: UnitDateHourRequest): Promise<GlobalResponseObject> {
        try {
            const inaDbConn = await this.shahiDb.getShahiDbConnectionForLineAndUnit(DbNamesEnum.IHS_SHARED,  req.unit, req.lineId);
            // get the records from the current table
            const alreadyMigrated = await this.migHelperService.checkIfTableAlreadyMigratedForDayHour(req.lineId,req.unit, AppTableNamesEnum.M_REASON, req.date, req.hour);
            if(alreadyMigrated) {
                throw new ErrorResponse(0, AppTableNamesEnum.M_REASON + 'Table already migrated for the date + hour ' + req.date + ' : ' + req.hour);
            }
            // get all the existing lines in the local db
            const lines = await this.reasonEnt.find({ select: ['lineId', 'reasonKey'], where: {unitId: req.unit}});
            const lineReasonMap = new Map<string, Set<string>>();
            lines.forEach(rec => {
                if(!lineReasonMap.has(rec.lineId)) {
                    lineReasonMap.set(rec.lineId, new Set<string>());
                }
                lineReasonMap.get(rec.lineId).add(rec.reasonKey);
            });


            // create a migration log record for the current table and the hour
            // await this.createTableMigratingLog(req.unit, AppTableNamesEnum.M_REASON, req.date, req.hour); 
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_REASON, req.date, req.hour, MigrationStatusEnum.PROGRESS);

            const reasonEnts: ReasonEntity[] = [];
            // get all the reasons from the SHAHI INA
            const query = `SELECT ${req.lineId} as LineId, WC_Key, WC_Description, WC_PA_Key FROM  ${this.dbName}.Work_Codes`;
            const result: {WC_Description: string, LineId: number, WC_PA_Key: string, WC_Key: number}[] = await inaDbConn.query(query);

            if(result.length == 0) {
                throw new ErrorResponse(0, 'No reasons data found for the date + hour ' + req.date + ' : ' + req.hour);
            }
            for(const rec of result) {
                const lineId = rec.LineId?.toString();
                const rKey = rec.WC_Key?.toString();
                if(!lineReasonMap.get(lineId)?.has(rKey)) {

                    const reasonEnt = new ReasonEntity();
                    reasonEnt.unitId = req.unit;
                    reasonEnt.lineId = lineId;
                    reasonEnt.reasonCode = rKey;
                    reasonEnt.reasonDesc = rec.WC_Description;
                    reasonEnt.reasonKey = rKey;
                    reasonEnt.level = rec.WC_PA_Key;

                    reasonEnts.push(reasonEnt);
                }
            }
            await this.reasonEnt.save(reasonEnts);

            // move the status to completed
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_REASON, req.date, req.hour, MigrationStatusEnum.COMPLETED);

            return new GlobalResponseObject(true, 0, 'Reasons plan data dumped');
        } catch (error) {
            console.log(error);
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.M_REASON, req.date, req.hour, MigrationStatusEnum.FAILED, error.message);
            return new GlobalResponseObject(false, 0, error.message);
        }
    }


    // to be handled later
    async createLineDayPlan(req: UnitDateHourRequest): Promise<GlobalResponseObject> {
        return new GlobalResponseObject(true, 0, 'Hour wise day plan data dumped');
    }


    // TESTED
    // create the line day actual track
    // NOTE: This job should exactly run at 59t minute of the hour
    // i.e if you want to get the track for hour 9-10, then this job should be exactly triggered at 09.59 to get all the transactions from 9-9.59
    // for the next hour it will trigger at 10.59 , 11.59 and so on...
    async createLineActualDayTrack(req: UnitDateHourRequest): Promise<GlobalResponseObject> {
        try {
            const inaHistoryDbConn = await this.shahiDb.getShahiDbConnectionForLineAndUnit(DbNamesEnum.IHS_HISTORY,  req.unit, req.lineId);
            const inaIHSDbConn = await this.shahiDb.getShahiDbConnectionForLineAndUnit(DbNamesEnum.IHS,  req.unit, req.lineId);
            const inaSharedDbConn = await this.shahiDb.getShahiDbConnectionForLineAndUnit(DbNamesEnum.IHS_SHARED,  req.unit, req.lineId);
            // get the records from the current table
            const alreadyMigrated = await this.migHelperService.checkIfTableAlreadyMigratedForDayHour(req.lineId,req.unit, AppTableNamesEnum.T_LINE_ACTUAL_DAY_TRACK, req.date, req.hour);
            if(alreadyMigrated) {
                throw new ErrorResponse(0, AppTableNamesEnum.T_LINE_ACTUAL_DAY_TRACK + ' : Table already migrated for the date + hour ' + req.date + ' : ' + req.hour);
            }
            // NOTE: This below query, we qre querying the data from the odp detail where 1 record will be created for 1 workstation + operator + operation.
            // So dynamically the completed garments quantity will be updated to the same record in the day. So we have to get the diff from our local table for the last 
            // inserted record for the last hour within the day
            const date = req.date;
            const hour = req.hour;

            const plantSubstitutingHour = await this.getPlantSubstitutingHour(hour, req.unit, date);

            let from = `${date} ${hour}:00:00`;
            let to = `${date} ${hour}:59:59`;
            const unitStartHour = await this.migHelperService.getFirstWorkingHourInUnitForDate(req.unit, req.date);
            if(hour == unitStartHour) {
                // for the first hour in the shift, we are putting a buffer of 30 mins before the shift time because some operators may start early
                from = `${date} ${hour-1}:30:00`;
            }

            // TESTED
            // get the operation level smvs
            const operationSmvMap = new Map<number, number>(); // op => smv
            // since we run this job at an hour level, so assuming that the style will be unique for an hour
            const workstationStyleMap = new Map<number, number>(); // workstation => style id
            const operationSmvQuery = `SELECT d.ODPD_OC_Key, d.ODPD_Standard , d.ODPD_ST_Key, d.ODPD_Workstation
                FROM ODP_Master m 
                LEFT JOIN ODP_Detail d ON m.ODP_Key = d.ODPD_ODP_Key 
                WHERE m.ODP_Last_Hanger_Time BETWEEN '${date} 00:00:00' AND '${date} 19:00:00' AND d.ODPD_WC_KEY = 22`;
            const operationSmvResult: {ODPD_OC_Key: number, ODPD_Standard: number, ODPD_ST_Key: number, ODPD_Workstation: number}[] = await inaIHSDbConn.query(operationSmvQuery);
            operationSmvResult.forEach(r => {
                operationSmvMap.set(r.ODPD_OC_Key, r.ODPD_Standard);
                
                workstationStyleMap.set(r.ODPD_Workstation, r.ODPD_ST_Key);
            });

            // TESTED
            // AND HRDH_ODP_Credited = 1 removed this line from the below query
            //get all the entries from the line history
            // const query = `SELECT ${req.lineId} as LineId,  d.HRDH_Workstation, d.HRDH_EM_Key, d.HRDH_OC_Key, count(1) as completed_quantity, 
            //     DATEPART(hour, d.HRDH_Completed_Time) as hour, SUM(DATEDIFF(ss, d.HRDH_Started_Time, d.HRDH_Completed_Time)) as actual_time, 1 as ODPD_Is_Overtime
            //     FROM IHS_HISTORY.dbo.Hanger_Routing_Details_History d
            //     WHERE d.HRDH_Completed_Time BETWEEN '${from}' AND '${to}'
            //      AND d.HRDH_OC_Completed = 1
            //     GROUP BY d.HRDH_Workstation, d.HRDH_OC_Key, d.HRDH_EM_Key, DATEPART(hour, d.HRDH_Completed_Time)
            //     ORDER BY hour, HRDH_Workstation ASC
            // `;
            const queryHistory = `SELECT ${req.lineId} as LineId, d.HRDH_Workstation as workstation, d.HRDH_EM_Key as employee_key, d.HRDH_OC_Key as operation_key,
                count(1) as completed_quantity, 
                DATEPART(hour, d.HRDH_Completed_Time) as hour, SUM(DATEDIFF(ss, d.HRDH_Started_Time, d.HRDH_Completed_Time)) as actual_time, 1 as ODPD_Is_Overtime,
                MIN(d.HRDH_Started_Time) as first_hanger_time, MAX(d.HRDH_Started_Time) as last_hanger_time
                FROM IHS_HISTORY.dbo.Hanger_Routing_Details_History d
                WHERE d.HRDH_Completed_Time BETWEEN '${from}' AND '${to}'
                AND d.HRDH_OC_Completed = 1
                AND  HRDH_Workstation > 0
                GROUP BY d.HRDH_Workstation, d.HRDH_OC_Key, d.HRDH_EM_Key, DATEPART(hour, d.HRDH_Completed_Time)
                ORDER BY hour, HRDH_Workstation ASC
            `;
            const queryActual = `SELECT ${req.lineId} as LineId, d.HRD_Workstation as workstation, d.HRD_EM_Key as employee_key, d.HRD_OC_Key as operation_key,
                count(1) as completed_quantity, 
                DATEPART(hour, d.HRD_Completed_Time) as hour, SUM(DATEDIFF(ss, d.HRD_Started_Time, d.HRD_Completed_Time)) as actual_time, 1 as ODPD_Is_Overtime,
                MIN(d.HRD_Started_Time) as first_hanger_time, MAX(d.HRD_Started_Time) as last_hanger_time
                FROM IHS.dbo.Hanger_Routing_Details d
                WHERE d.HRD_Completed_Time BETWEEN '${from}' AND '${to}'
                AND d.HRD_OC_Completed = 1
                AND  HRD_Workstation > 0
                GROUP BY d.HRD_Workstation, d.HRD_OC_Key, d.HRD_EM_Key, DATEPART(hour, d.HRD_Completed_Time)
                ORDER BY hour, HRD_Workstation ASC
            `;
            const actualResult: {LineId: number, workstation: number, employee_key: number, completed_quantity: number, operation_key: number, hour: number, actual_time: number, ODPD_Is_Overtime: number,  first_hanger_time: string, last_hanger_time: string}[] = await inaHistoryDbConn.query(queryActual);
            const historyResult: {LineId: number, workstation: number, employee_key: number, completed_quantity: number, operation_key: number, hour: number, actual_time: number, ODPD_Is_Overtime: number,  first_hanger_time: string, last_hanger_time: string}[] = await inaHistoryDbConn.query(queryHistory);
            const clubbedHangerDetailsMap = new Map<string, {completed_quantity: number, actual_time: number, first_hanger_time: string, last_hanger_time: string}>();
            // club both the results and bring them as 1 set grouping by workstation, operation, employee, 
            historyResult.forEach(rec => {
                const key = rec.LineId+'@'+rec.workstation+'@'+rec.operation_key+'@'+rec.employee_key+'@'+rec.hour;
                if(!clubbedHangerDetailsMap.has(key)) {
                    clubbedHangerDetailsMap.set(key, {completed_quantity: 0, actual_time: 0, first_hanger_time: rec.first_hanger_time, last_hanger_time: rec.last_hanger_time});
                }
                clubbedHangerDetailsMap.get(key).actual_time += rec.actual_time;
                clubbedHangerDetailsMap.get(key).completed_quantity += rec.completed_quantity;
            });
            // now add the entires from the actual hanger_routing as well
            actualResult.forEach(rec => {
                const key = rec.LineId+'@'+rec.workstation+'@'+rec.operation_key+'@'+rec.employee_key+'@'+rec.hour;
                if(!clubbedHangerDetailsMap.has(key)) {
                    clubbedHangerDetailsMap.set(key, {completed_quantity: 0, actual_time: 0, first_hanger_time: rec.first_hanger_time, last_hanger_time: rec.last_hanger_time});
                }
                clubbedHangerDetailsMap.get(key).actual_time += rec.actual_time;
                clubbedHangerDetailsMap.get(key).completed_quantity += rec.completed_quantity;
            });
            // iterate the map and construct the same DB result for the clubbed result
            const finalClubbedResult: {LineId: number, workstation: number, employee_key: number, completed_quantity: number, operation_key: number, hour: number, actual_time: number, ODPD_Is_Overtime: number, first_hanger_time: string, last_hanger_time: string} [] = [];
            clubbedHangerDetailsMap.forEach((vals, key) => {
                // const key = rec.LineId+'@'+rec.workstation+'@'+rec.operation_key+'@'+rec.employee_key+'@'+rec.hour;
                const params = key.split('@');
                const workstationId = Number(params[1]);
                const operationId = Number(params[2]); 
                const employeeKey = Number(params[3]);
                const hour = Number(params[4]);
                
                finalClubbedResult.push(
                    {LineId: Number(req.lineId), workstation: workstationId, employee_key: employeeKey, completed_quantity: vals.completed_quantity, operation_key: operationId, hour: hour, actual_time: vals.actual_time, ODPD_Is_Overtime: 1, first_hanger_time: vals.first_hanger_time, last_hanger_time: vals.last_hanger_time}
                );
            });
            if(finalClubbedResult.length == 0) {
                throw new ErrorResponse(0, 'No line data found for the date + hour ' + req.date + ' : ' + req.hour);
            }
            // create a migration log record for the current table and the hour
            // await this.createTableMigratingLog(req.unit, AppTableNamesEnum.T_LINE_ACTUAL_DAY_TRACK, req.date, req.hour);
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.T_LINE_ACTUAL_DAY_TRACK, req.date, req.hour, MigrationStatusEnum.PROGRESS);

            // TESTED
            // Last workstation query - to figure out the last operation
            const lastWorkstationQuery = `SELECT  ${req.lineId} as LineId, MAX(d.ODPD_Workstation) as last_work 
                FROM  ${this.dbName}.ODP_Master m
                LEFT JOIN  ${this.dbName}.ODP_Detail d ON m.ODP_Key = d.ODPD_ODP_Key 
                WHERE m.ODP_Last_Hanger_Time BETWEEN '${date} 00:00:00' AND '${date} 23:00:00' `;
            const lastWorkstationResult: {LineId: number, last_work: number}[] = await inaIHSDbConn.query(lastWorkstationQuery);
            const lineLastWorkMap = new Map<number, number>();
            // set the max workstation id
            for(const rec of lastWorkstationResult) {
                lineLastWorkMap.set(rec.LineId, rec.last_work);
            }
            const lineReworkCompletedMap = new Map<string, {totalReworkCompleted: number, totalSecs: number}>(); // line+operation+emp -> rework completed quantity (for the current hour only)
            const lineReworkRaisedMap = new Map<string, {totalReworkRaised: number, totalSecs: number}>(); // line+workstation+emp -> rework raised quantity (for the current hour only)

            // TESTED
            // Rework info - at which time and which workstation the rework is initialized (this also impacts current operation productivity)
            const reworkTriggeredQuery = `SELECT  ${req.lineId} as LineId, r.QCR_Defect_OC_Key, r.QCR_Repair_EM_Key, r.QCR_HM_KEY, r.QCR_HM_ID, r.QCR_Repair_DateTime, 
                r.QCR_From_QC_Station, QCR_Sent_To_Rework_By_EM_Key
                FROM  ${this.dbName}.QC_Rework r WHERE QCR_Defect_DateTime BETWEEN '${from}' AND '${to}' AND QCR_Defect_Quantity = 1`;
            const reworkTriggeredResult: {LineId: number, QCR_Defect_OC_Key: number, QCR_Repair_EM_Key: number, QCR_HM_KEY: string, QCR_HM_ID: string, QCR_From_QC_Station: number, QCR_Sent_To_Rework_By_EM_Key: number}[] = await inaSharedDbConn.query(reworkTriggeredQuery);
            for(const tr of reworkTriggeredResult) {
                // line + work + op + emp
                const key = tr.LineId+'@'+ tr.QCR_From_QC_Station+'@'+tr.QCR_Sent_To_Rework_By_EM_Key;
                if(!lineReworkRaisedMap.has(key)) {
                    lineReworkRaisedMap.set(key, {totalReworkRaised: 0, totalSecs: 0});
                }
                lineReworkRaisedMap.get(key).totalReworkRaised += 1;
            }
            // TESTED
            // Rework info - at which time the rework is been completed
            const reworkDoneQuery = `SELECT  ${req.lineId} as LineId, r.QCR_Defect_OC_Key, r.QCR_Repair_EM_Key, r.QCR_HM_KEY, r.QCR_HM_ID, r.QCR_Repair_DateTime
                FROM QC_Rework r WHERE QCR_Repair_DateTime BETWEEN '${from}' AND '${to}' AND QCR_Repair_Quantity = 1`;
            const reworkDoneResult: {LineId: number, QCR_Defect_OC_Key: number, QCR_Repair_EM_Key: number, QCR_HM_KEY: string, QCR_HM_ID: string}[] =  await inaSharedDbConn.query(reworkDoneQuery);
            for(const rew of reworkDoneResult) {
                const hmKey = rew.QCR_HM_KEY;
                const hmId = rew.QCR_HM_ID;
                // line + work + op + emp
                const key = rew.LineId+'@'+rew.QCR_Defect_OC_Key+'@'+rew.QCR_Repair_EM_Key;
                if(!lineReworkCompletedMap.has(key)) {
                    lineReworkCompletedMap.set(key, {totalReworkCompleted: 0, totalSecs: 0});
                }
                const filter = hmKey ? `HRDH_HMH_Key = '${hmKey}'` : `HRDH_HMH_Key = null`;
         
                let totalSecs = 0;

                // get the rework time of the garment form the ina hanger history records
                const reworkTimeQueryHistory = `SELECT DATEDIFF(ss, HRDH_Started_Time, HRDH_Completed_Time ) as rework_secs, HRDH_Completed_Time as completed_time, HRDH_Started_Time as started_time, HRDH_Comments as comments
                    FROM ${this.dbName}.Hanger_Routing_Details_History
                    WHERE ${filter} AND HRDH_Completed_Time >= '${from}'
                    AND HRDH_EM_Key = ${rew.QCR_Repair_EM_Key} AND HRDH_ODP_Credited = 0`;
                let reworkTimeResult: {rework_secs: number, completed_time: string, started_time: string, comments: string}[] = [];
                reworkTimeResult = await inaHistoryDbConn.query(reworkTimeQueryHistory);
                if(reworkTimeResult.length == 0) {
                    // If the info is not found in the history then go to the actual transaction routing tables
                    // then query the info from the transaction IHS
                    const reworkTimeTransQuery = `SELECT DATEDIFF(ss, HRD_Started_Time, HRD_Completed_Time ) as rework_secs, HRD_Completed_Time as completed_time, 
                        HRD_Started_Time as started_time, HRD_Comments as comments
                        FROM Hanger_Routing_Details 
                        WHERE HRD_HM_ID = '${hmId}' AND HRD_Completed_Time >= '${from}'
                        AND HRD_EM_Key = ${rew.QCR_Repair_EM_Key} AND HRD_ODP_Credited = 0`;
                    // Re assign the same rework result to the variable
                    reworkTimeResult = await inaIHSDbConn.query(reworkTimeTransQuery);
                }
                reworkTimeResult.forEach(rt => {
                    totalSecs += rt.rework_secs;
                });

                lineReworkCompletedMap.get(key).totalReworkCompleted += 1;
                lineReworkCompletedMap.get(key).totalSecs += totalSecs;
            }
            const alreadyExistingQtysMap = new Map<string, {qty: number, spentSecs: number}>();
            // get the todays last inserted records 
            const existingLineTrack =  await this.lineActualDayTrackEnt.find({select: ['lineId', 'goodQuantity', 'actualSpentTime', 'operationId', 'operatorId', 'workstationId'], where: {unitId: req.unit, planDate: req.date }});
            existingLineTrack.forEach(r => {
                const key = r.lineId+'@'+r.operationId+'@'+r.workstationId+'@'+r.operatorId;
                if(!alreadyExistingQtysMap.has(key)) {
                    alreadyExistingQtysMap.set(key, { qty: 0, spentSecs: 0});
                }
                const p = alreadyExistingQtysMap.get(key);
                alreadyExistingQtysMap.set(key, { qty: p.qty + r.goodQuantity, spentSecs: p.spentSecs + r.actualSpentTime});
            });
            const lineDayActualTrackEntities: LineActualDayTrackEntity[] = [];
            for(const rec of finalClubbedResult) {
                const key = rec.LineId+'@'+rec.operation_key+'@'+rec.workstation+'@'+rec.employee_key;
                const preData = alreadyExistingQtysMap.get(key);
                let spentSecs = 0;
                let goodQty = 0;
                // if it is an overtime, then the spent time and garments should be populated as is. no diff should be calculated
                if(preData && !rec.ODPD_Is_Overtime) {
                    // goodQty = rec.ODPD_Quantity - preData.qty;
                    // spentSecs = rec.ODPD_Actual_Time - preData.spentSecs;
                    goodQty = 0;
                    spentSecs = 0;
                } else {
                    goodQty = rec.completed_quantity;
                    spentSecs = rec.actual_time;
                }

                const style = workstationStyleMap.get(rec.workstation);
                const smv = operationSmvMap.get(rec.operation_key);

                // get the previous hour record from the local DB
                const ldat = new LineActualDayTrackEntity();
                ldat.unitId = req.unit;
                ldat.styleId = style?.toString();
                ldat.operationId = rec.operation_key?.toString();
                ldat.operationSmv = smv;
                ldat.workstationId = rec.workstation?.toString();
                ldat.isRework = false;
                ldat.operatorId = rec.employee_key?.toString();
                ldat.firstTransInHour = "00:00:00" // rec.first_hanger_time;
                ldat.lastTransInHour = "00:00:00"  // rec.last_hanger_time;
                ldat.hour = plantSubstitutingHour;
                ldat.actualHour = req.hour;
                ldat.isRejection = false;
                ldat.rejectedQuantity = 0;
                ldat.isLastOp = lineLastWorkMap.get(rec.LineId) == rec.workstation; // set the last op when the last workstation is identified
                ldat.goodQuantity = goodQty;
                ldat.actualSpentTime = spentSecs;
                ldat.lineId = rec.LineId?.toString();
                ldat.planDate = req.date;
                // ldat.overtime = rec.ODPD_Is_Overtime;
                ldat.overtime = false;

                const rewCompKey = rec.LineId+'@'+rec.operation_key+'@'+rec.employee_key;
                const rewRaisedKey = rec.LineId+'@'+rec.workstation+'@'+rec.employee_key;
                if(lineReworkRaisedMap.has(rewRaisedKey)) {
                    // attach the rework raised quantity
                    ldat.reworkRaisedQuantity = lineReworkRaisedMap.get(rewRaisedKey).totalReworkRaised;
                }
                
                lineDayActualTrackEntities.push(ldat);

                if(lineReworkCompletedMap.has(rewCompKey)) {
                    // this means this has a rework for this hour + workstation + operation + employee
                    const reworkInfo = lineReworkCompletedMap.get(rewCompKey);
                    const lineClone: LineActualDayTrackEntity = JSON.parse(JSON.stringify(ldat));
                    lineClone.rejectedQuantity = 0;
                    lineClone.reworkRaisedQuantity = 0;
                    lineClone.isRework = true;
                    lineClone.goodQuantity = reworkInfo?.totalReworkCompleted ?? 0;
                    lineClone.actualSpentTime = reworkInfo?.totalSecs ?? 0 // have to be calculated from INA hanger
                    lineDayActualTrackEntities.push(lineClone);
                }
            }
            await this.lineActualDayTrackEnt.save(lineDayActualTrackEntities, {reload: false});

            // save the plan here
            const planReq = new UnitDateHourRequest();
            planReq.unit = req.unit, planReq.date = req.date;
            // this.createPlan(planReq);

            await this.createOperatorEfficiency(req);
            await this.createLineEfficiency(req);
            // move the status to completed
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.T_LINE_ACTUAL_DAY_TRACK, req.date, req.hour, MigrationStatusEnum.COMPLETED);

            return new GlobalResponseObject(true, 0, 'Line day wise actual data dumped');
        } catch (error) {
            console.log(error);
            await this.createOperatorEfficiency(req);
            await this.createLineEfficiency(req);
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.T_LINE_ACTUAL_DAY_TRACK, req.date, req.hour, MigrationStatusEnum.FAILED, error.message);
            return new GlobalResponseObject(false, 0, error.message);
        }
    }

    // To be tested
    // create the downtime in the plant
    async createLineDowntime(req: UnitDateHourRequest): Promise<GlobalResponseObject> {
        try {
            const inaDbConn = await this.shahiDb.getShahiDbConnectionForLineAndUnit(DbNamesEnum.IHS,  req.unit, req.lineId);
            // get the records from the current table
            const alreadyMigrated = await this.migHelperService.checkIfTableAlreadyMigratedForDayHour(req.lineId, req.unit, AppTableNamesEnum.T_LINE_DOWN_TIME, req.date, req.hour);
            if(alreadyMigrated) {
                throw new ErrorResponse(0, AppTableNamesEnum.T_LINE_DOWN_TIME + ' : Table already migrated for the date + hour ' + req.date + ' : ' + req.hour);
            }

            const date = req.date;
            const hour = req.hour;

            const plantSubstitutingHour = await this.getPlantSubstitutingHour(hour, req.unit, date);

            let from = `${date} ${hour}:00:00`;
            let to = `${date} ${hour}:59:59`;
            const unitStartHour = await this.migHelperService.getFirstWorkingHourInUnitForDate(req.unit, req.date);
            if(hour == unitStartHour) {
                // for the first hour in the shift, we are putting a buffer of 30 mins before the shift time because some operators may start early
                from = `${date} ${hour-1}:30:00`;
            }
            
            // create a migration log record for the current table and the hour
            // await this.createTableMigratingLog(req.unit, AppTableNamesEnum.T_LINE_DOWN_TIME, req.date, req.hour);
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.T_LINE_DOWN_TIME, req.date, req.hour, MigrationStatusEnum.PROGRESS);

            const downTimeKeys = new Set<string>();
            // get all the mandatory downtimes
            const downTimeCodes = await this.reasonEnt.find({select: ['lineId', 'reasonKey', 'reasonDesc'], where: {level: In([ReasonPriorityEnum.GUARANTEE, ReasonPriorityEnum.MINWAGE, ReasonPriorityEnum.AVERAGE]), unitId: req.unit} }); 
            const downTimeCodesMap = new Map<string, string>();
            downTimeCodes.forEach(p => {
                const key = p.lineId+'@'+p.reasonKey;
                downTimeCodesMap.set(key, p.reasonDesc);

                downTimeKeys.add(p.reasonKey);
            });
            const downTimeCodesString = downTimeKeys?.size > 0 ? Array.from(downTimeKeys).toString() : '';

            const downTimeQuery = `SELECT ${req.lineId} as LineId, d.ODPD_Workstation, d.ODPD_WC_Key, d.ODPD_Quantity, d.ODPD_ST_Key, d.ODPD_OC_Key, d.ODPD_Standard, 
                d.ODPD_Actual_Time, d.ODPD_STPO_Key,
                m.ODP_EM_Key, m.ODP_First_Hanger_Time, m.ODP_Last_Hanger_Time, m.ODP_Shift_Clock_In, m.ODP_Shift_Clock_Out, d.ODPD_Is_Overtime
                FROM ODP_Detail d
                LEFT JOIN ODP_Master m on m.ODP_Key = d.ODPD_ODP_Key
                WHERE m.ODP_Shift_Clock_In BETWEEN '${date} 00:00:00' AND '${date} 23:00:00' 
                AND d.ODPD_WC_Key IN (${downTimeCodesString}) 
                AND m.ODP_Last_Hanger_Time
                BETWEEN '${from}' AND '${to}'
                ORDER BY d.ODPD_Workstation`;
            const downTimeResult: {LineId: number, ODPD_ST_Key: number, ODPD_Workstation: number, ODPD_OC_Key: number, ODPD_Standard: number, ODPD_Actual_Time: number, ODP_EM_Key: number, ODPD_WC_Key: number }[] = await inaDbConn.query(downTimeQuery);
            if(downTimeResult.length == 0) {
                throw new ErrorResponse(0, 'No line downtime data found for the date + hour ' + req.date + ' : ' + req.hour);
            }
            
            const downTimeEnts: LineDownTimeEntity[] = [];
            for(const rec of downTimeResult) {
                const downTimeEnt = new LineDownTimeEntity();

                const key = rec.LineId+'@'+rec.ODPD_WC_Key;

                if(downTimeCodesMap.get(key)) {
                    downTimeEnt.unitId = req.unit;
                    downTimeEnt.lineId = rec.LineId?.toString();
                    downTimeEnt.workstationId = rec.ODPD_Workstation?.toString();
                    downTimeEnt.operationId = rec.ODPD_OC_Key?.toString();
                    downTimeEnt.operatorId = rec.ODP_EM_Key?.toString();
                    downTimeEnt.operationSmv = rec.ODPD_Standard;
                    downTimeEnt.styleId = rec.ODPD_ST_Key?.toString();
                    downTimeEnt.downtimeReason = downTimeCodesMap.get(key) ?? 'NOT FOUND';
                    downTimeEnt.downtimeCode = rec.ODPD_WC_Key?.toString(); // 22
                    downTimeEnt.downtime = rec.ODPD_Actual_Time;
                    downTimeEnt.planDate = req.date;
                    downTimeEnt.hour = plantSubstitutingHour;
                    downTimeEnt.actualHour = hour;
                    downTimeEnts.push(downTimeEnt);
                }
            }
            await this.lineDownEnt.save(downTimeEnts, {reload: false});

            // move the status to completed
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.T_LINE_DOWN_TIME, req.date, req.hour, MigrationStatusEnum.COMPLETED);

            return new GlobalResponseObject(true, 0, 'Hour wise down time data dumped');
        } catch (error) {
            console.log(error);
            // move the status to completed
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.T_LINE_DOWN_TIME, req.date, req.hour, MigrationStatusEnum.FAILED, error.message);
            return new GlobalResponseObject(false, 0, error.message);
        }
    }

    
    // create the operator efficiency
    async createOperatorEfficiency(req: UnitDateHourRequest): Promise<GlobalResponseObject> {
        try {
            // get the records from the current table
            const alreadyMigrated = await this.migHelperService.checkIfTableAlreadyMigratedForDayHour(req.lineId,req.unit, AppTableNamesEnum.T_OPERATOR_EFF_DAY_TRACK, req.date, req.hour);
            if(alreadyMigrated) {
                throw new ErrorResponse(0, AppTableNamesEnum.T_OPERATOR_EFF_DAY_TRACK + ' : Table already migrated for the date + hour ' + req.date + ' : ' + req.hour);
            }

            const opEffTrackEnts: OperatorEfficiencyDayTrackEntity[] = [];

            const query =  await this.lineActualDayTrackEnt.createQueryBuilder('ldat')
            .select('unit_id, line_id, plan_date, style_id, workstation_id, operation_id, operator_id, hour, actual_hour, operation_smv, sum(good_quantity) as good_qty, SUM(actual_spent_time) as actual_spent_time, is_last_op')
            .where(`unit_id = '${req.unit}' AND plan_date = '${req.date}' AND hour = ${req.hour} AND is_rework = false AND line_id = '${req.lineId}' `)
            .groupBy('unit_id, line_id, plan_date, style_id, workstation_id, operation_id, operator_id, hour');
            const result: {unit_id: string, line_id: string, plan_date: string, style_id: string, workstation_id: string, operation_id: string, operator_id: string, hour: number, actual_hour: number, operation_smv: number, good_qty: number, actual_spent_time: number, is_last_op: boolean}[] = await query.getRawMany();

            const currentHourRecs = await this.lineActualDayTrackEnt.find({ where: { planDate: req.date, actualHour: req.hour, isRework: false, isRejection: false, unitId: req.unit} });
            if (currentHourRecs.length == 0) {
                throw new ErrorResponse(0, 'Employee efficiency data not found ' + req.date + ' : ' + req.hour);
            }
            // create a migration log record for the current table and the hour
            // await this.createTableMigratingLog(req.unit, AppTableNamesEnum.T_OPERATOR_EFF_DAY_TRACK, req.date, req.hour);
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.T_OPERATOR_EFF_DAY_TRACK, req.date, req.hour, MigrationStatusEnum.PROGRESS);

            for(const rec of result) {
                rec.actual_spent_time = Number(rec.actual_spent_time);
                rec.good_qty = Number(rec.good_qty);

                const opEffEnt = new OperatorEfficiencyDayTrackEntity();
                opEffEnt.unitId = rec.unit_id;
                opEffEnt.lineId = rec.line_id;
                opEffEnt.workstationId = rec.workstation_id;
                opEffEnt.styleId = rec.style_id;
                opEffEnt.operationId = rec.operation_id;
                opEffEnt.operatorId = rec.operator_id;
                opEffEnt.hour = rec.hour;
                opEffEnt.actualHour = rec.actual_hour;
                opEffEnt.planDate = rec.plan_date;

                opEffEnt.hourPlannedTime = 0;
                opEffEnt.actualPlannedTime = Number(rec.good_qty) * Number(rec.operation_smv); 
                // TODO - remove the 7.5
                // opEffEnt.efficiency = Math.round( (opEffEnt.actualPlannedTime / (3600 * 7.5) ) * 100);
                let divisor = 0;

                const hourWiseBreakTimes = await this.migHelperService.getUnitBreakTimesForDay(req.unit, req.date);
                const currHourBreakTimeInMinutes = hourWiseBreakTimes.get(rec.hour);
                if(currHourBreakTimeInMinutes) {
                    divisor = (60 - currHourBreakTimeInMinutes) * 60;
                } else {
                    divisor = 3600;
                }
                // TODO: Efficiency to be changed - central dashboard identified
                opEffEnt.efficiency = Math.round( (opEffEnt.actualPlannedTime / (divisor) ) * 100);
                opEffEnt.actualSpentTime = rec.actual_spent_time; // the time spent by the operator in the workstation + line
                opEffEnt.isLastOp = rec.is_last_op;

                opEffTrackEnts.push(opEffEnt);
            }

            await this.empEffEnt.save(opEffTrackEnts, {reload: false});
            // move the status to completed
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.T_OPERATOR_EFF_DAY_TRACK, req.date, req.hour, MigrationStatusEnum.COMPLETED);

            return new GlobalResponseObject(true, 0, 'Operator efficiency data dumped');
        } catch (error) {
            console.log(error);
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.T_OPERATOR_EFF_DAY_TRACK, req.date, req.hour, MigrationStatusEnum.FAILED, error.message);
            return new GlobalResponseObject(false, 0, error.message);
        }
    }


    // creates the line wise average efficiency
    async createLineEfficiency(req: UnitDateHourRequest): Promise<GlobalResponseObject> {
        try {
            // get the records from the current table
            const alreadyMigrated = await this.migHelperService.checkIfTableAlreadyMigratedForDayHour(req.lineId, req.unit, AppTableNamesEnum.T_LINE_EFF_DAY_TRACK, req.date, req.hour);
            if(alreadyMigrated) {
                throw new ErrorResponse(0, AppTableNamesEnum.T_LINE_EFF_DAY_TRACK + ' : Table already migrated for the date + hour ' + req.date + ' : ' + req.hour);
            }

            const query =  await this.empEffEnt.createQueryBuilder('1')
                .select('unit_id, line_id, hour, actual_hour, style_id, plan_date, SUM(efficiency) as efficiency, count(distinct workstation_id) as total_workstations')
                .where(`unit_id = '${req.unit}' AND plan_date = '${req.date}' AND actual_hour = '${req.hour}' AND line_id = '${req.lineId}' `)
                .groupBy('unit_id, line_id, hour');
            const result: {unit_id: string, line_id: string, style_id: string, hour: number, actual_hour: number, plan_date: string, efficiency: number, total_workstations: number}[] = await query.getRawMany();

            if (result.length == 0) {
                throw new ErrorResponse(0, 'Employee efficiency data not found ' + req.date + ' : ' + req.hour);
            }

            // create a migration log record for the current table and the hour
            // await this.createTableMigratingLog(req.unit, AppTableNamesEnum.T_LINE_EFF_DAY_TRACK, req.date, req.hour);
            
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.T_LINE_EFF_DAY_TRACK, req.date, req.hour, MigrationStatusEnum.PROGRESS);


            const effEnts: LineEfficiencyDayTrackEntity[] = [];
            for(const rec of result) {
                const avgEff = rec.total_workstations > 0 ? Math.round(rec.efficiency / rec.total_workstations) : 0;

                const ent = new LineEfficiencyDayTrackEntity();
                ent.unitId = rec.unit_id;
                ent.lineId = rec.line_id;
                ent.hour = rec.hour;
                ent.actualHour = rec.actual_hour;
                ent.planDate = rec.plan_date;
                ent.efficiency = avgEff;

                ent.garmentSmv = 0;
                ent.planQuantity = 0;
                ent.actualSmv = 0;

                effEnts.push(ent);
            }

            await this.lineEffEnt.save(effEnts, {reload: false});
            // move the status to completed
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.T_LINE_EFF_DAY_TRACK, req.date, req.hour, MigrationStatusEnum.COMPLETED);

            return new GlobalResponseObject(true, 0, 'Line average efficiency data dumped');
        } catch (error) {
            console.log(error);
            await this.migHelperService.updateMigrationLog(req.lineId, req.unit, AppTableNamesEnum.T_LINE_EFF_DAY_TRACK, req.date, req.hour, MigrationStatusEnum.FAILED, error.message);
            return new GlobalResponseObject(false, 0, error.message);
        }
    }

    /**
     * TODO: to be removed
     * @param req 
     * @returns 
     */
    async createPlanOld(req: UnitDateHourRequest): Promise<GlobalResponseObject> {

        const planQtys = {
            "23" : {
                1 : 1000,
                2 : 1000,
                3 : 1200,
                4 : 1200,
                5 : 1000,
                6 : 1100,

                11 : 1100,
                12 : 1200,
                13 : 1200,
                14 : 1200,
                15 : 1000,

                20 : 1100,
                21 : 1100,
                23 : 1100,
                24 : 1000
            },"37" : {
                1 : 1000,
                2 : 1000,
                3 : 1200,
                4 : 1200,
                5 : 1000,
                6 : 1100,
                7 : 1100,
                8 : 1100,
                10 : 1000,
                11 : 1100,
                12 : 1200,
                13 : 1200,
                14 : 1200
            },"54" : {
                14 : 1000,
                15 : 1000,
                16 : 1200,
                17 : 1200,
                18 : 1000,
                19 : 1100,
                20 : 1100,
                21 : 1100,
                22 : 1100,
                23 : 1100
            }
        };

        const linePlanEnts: LineDayPlanEntity[] = [];
        let units: string[] = [];
        if(req.unit) {
            units = [req.unit];
        } else {
            const allUnits = await this.migHelperService.getAllUnits();
            allUnits.forEach(u => {
                units.push(u.unitId);
            })
        }
        for(const unit of units) {
            const query = await this.lineActualDayTrackEnt.createQueryBuilder('ef')
            .select('line_id, style_id, plan_date')
            .where(`plan_date = '${req.date}' AND unit_id = '${unit}'`)
            .groupBy('line_id');
            const result: {line_id: string, style_id: string, plan_date: string}[] = await query.getRawMany();
            const unitHours = await this.migHelperService.getUnitActualWorkingHoursForDay(unit, req.date);
            const lastWorkingHour = unitHours[unitHours.length - 1];
            // get the per hour working minutes for the unit for a given date
            const perHourWorkingMinutes = await this.migHelperService.getUntiPlannedWorkingMinutesPerHoursForDay(unit, req.date);
            let totalMins = 0;
            perHourWorkingMinutes.forEach(hr => {
                totalMins += hr;
            });
            for(const rec of result) {
                // if the plan is already saved for the line, then dont save it again
                const linePlan = await this.linePlanEnt.findOne({ select: ['id'], where: { unitId: unit, planDate: req.date, lineId: rec.line_id}});
                if(linePlan){
                  continue;
                }

                let lineDayQty = planQtys[unit] ? planQtys[unit][rec.line_id] ? planQtys[unit][rec.line_id] : 1000 : 1000; // default to 1000 if no plan is saved
                let perMinQty = Number((lineDayQty/totalMins).toFixed(3));
                // let pendingQty = lineDayQty - (perMinQty * totalMins);
                // console.log(pendingQty);
                let consumedQty = 0;
                unitHours.forEach(hr => {
                    const currHourWorkingMins = perHourWorkingMinutes.get(hr);
                    const ent = new LineDayPlanEntity();
                    ent.planDate = rec.plan_date;
                    ent.unitId = unit;
                    ent.lineId = rec.line_id;
                    ent.styleId = rec.style_id;
                    ent.planQuantity = Math.floor(perMinQty * currHourWorkingMins);
                    // assign all the pending qty to the last hour
                    if(hr == lastWorkingHour) {
                        // ent.planQuantity = perMinQty * currHourWorkingMins + consumedQty;
                        ent.planQuantity = lineDayQty - consumedQty;
                    }
                    ent.buyer = 'NO BUYER';
                    // ent.buyer = Number(rec.line_id) < 5 ? 'Reliance' : Number(rec.line_id) < 10 ? 'DNMX' : 'H&M';
                    ent.hour = hr;
                    consumedQty += Math.floor(ent.planQuantity);
                    linePlanEnts.push(ent);
                });
            }
        }
        if(linePlanEnts.length > 0) {
            await this.linePlanEnt.save(linePlanEnts, {reload: false})
        }
        return new GlobalResponseObject(true, 0, 'Plan saved');
    }

    /**
     * INPROGRESS - New enhancement for the actual garment stitch time per garment
     * @param req 
     * @returns 
     */
    async createGarmentSmv(req: UnitDateHourRequest): Promise<GlobalResponseObject> {
        const inaHistoryDbConn = await this.shahiDb.getShahiDbConnectionForLineAndUnit(DbNamesEnum.IHS_HISTORY,  req.unit, req.lineId);
        const inaIHSDbConn = await this.shahiDb.getShahiDbConnectionForLineAndUnit(DbNamesEnum.IHS,  req.unit, req.lineId);
        const inaSharedDbConn = await this.shahiDb.getShahiDbConnectionForLineAndUnit(DbNamesEnum.IHS_SHARED,  req.unit, req.lineId);
        // get the records from the current table
        const alreadyMigrated = await this.migHelperService.checkIfTableAlreadyMigratedForDayHour(req.lineId,req.unit, AppTableNamesEnum.T_LINE_ACTUAL_DAY_TRACK, req.date, req.hour);
        if(alreadyMigrated) {
            throw new ErrorResponse(0, AppTableNamesEnum.T_LINE_ACTUAL_DAY_TRACK + ' : Table already migrated for the date + hour ' + req.date + ' : ' + req.hour);
        }

        // NOTE: This below query, we qre querying the data from the odp detail where 1 record will be created for 1 workstation + operator + operation.
        // So dynamically the completed garments quantity will be updated to the same record in the day. So we have to get the diff from our local table for the last 
        // inserted record for the last hour within the day
        const date = req.date;
        const hour = req.hour;

        let from = `${date} ${hour}:00:00`;
        let to = `${date} ${hour}:59:59`;
        // if(hour == this.shiftStartHour) {
        //     // for the first hour in the shift, we are putting a buffer of 30 mins before the shift time because some operators may start early
        //     from = `${date} ${hour-1}:30:00`;
        // }

        const queryHistory = `SELECT ${req.lineId} as LineId, d.HRDH_Workstation as workstation, d.HRDH_EM_Key as employee_key, d.HRDH_OC_Key as operation_key,
                count(1) as completed_quantity, 
                DATEPART(hour, d.HRDH_Completed_Time) as hour, SUM(DATEDIFF(ss, d.HRDH_Started_Time, d.HRDH_Completed_Time)) as actual_time, 1 as ODPD_Is_Overtime
                FROM IHS_HISTORY.dbo.Hanger_Routing_Details_History d
                WHERE d.HRDH_Completed_Time BETWEEN '${from}' AND '${to}'
                AND d.HRDH_OC_Completed = 1
                GROUP BY d.HRDH_EM_Key, DATEPART(hour, d.HRDH_Completed_Time), 
                ORDER BY hour, HRDH_Workstation ASC
        `;
        const queryActual = `SELECT ${req.lineId} as LineId, d.HRD_Workstation as workstation, d.HRD_EM_Key as employee_key, d.HRD_OC_Key as operation_key,
            count(1) as completed_quantity, 
            DATEPART(hour, d.HRD_Completed_Time) as hour, SUM(DATEDIFF(ss, d.HRD_Started_Time, d.HRD_Completed_Time)) as actual_time, 1 as ODPD_Is_Overtime
            FROM IHS.dbo.Hanger_Routing_Details d
            WHERE d.HRD_Completed_Time BETWEEN '${from}' AND '${to}'
            AND d.HRD_OC_Completed = 1
            GROUP BY d.HRD_Workstation, d.HRD_OC_Key, d.HRD_EM_Key, DATEPART(hour, d.HRD_Completed_Time)
            ORDER BY hour, HRD_Workstation ASC
        `;

        const actualResult: {LineId: number, workstation: number, employee_key: number, completed_quantity: number, operation_key: number, hour: number, actual_time: number, ODPD_Is_Overtime: number}[] = await inaHistoryDbConn.query(queryActual);
        const historyResult: {LineId: number, workstation: number, employee_key: number, completed_quantity: number, operation_key: number, hour: number, actual_time: number, ODPD_Is_Overtime: number}[] = await inaHistoryDbConn.query(queryHistory);


        return new GlobalResponseObject(true, 0 , 'Garment SMV saved successfully');
    }


    async getPlantSubstitutingHour(currentHour: number, unitId: string, date: string): Promise<number> {
        const unitWrokingHours = await this.migHelperService.getUnitConfiguredWorkingHoursForDay(unitId, date);
        const startHour = unitWrokingHours[0];
        const endHour = unitWrokingHours[unitWrokingHours.length - 1];
        if(currentHour < Number(startHour)) {
            return Number(startHour);
        } else if(currentHour > Number(endHour)) {
            return Number(endHour);
        } else {
            return currentHour;
        }
    }


    async getPlanTemplate(req: UnitDateHourRequest): Promise<any> {
        const tempObj: TempUnitModel[] = [];
        const units = await this.migHelperService.getAllUnits();
        for(const unit of units) {
            if(req.unit) {
                req.unit = unit.unitId;
                continue;
            }
            const tempUnitLines: TempLineModel[] = [];
            // get all the lines in the unit
            const plantConfiguredWorkingHours = unit.workingHours.split(',');
            const linesInUnit = await this.lineEnt.find({select: ['lineId', 'lineNumber'], where: { unitId: unit.unitId, isActive: true }});
            linesInUnit.forEach(line => {
                tempUnitLines.push(
                    new TempLineModel(line.lineId, line.lineNumber, 0)
                );
            });

            tempObj.push(new TempUnitModel(unit.unitId, unit.workingHours, "YYYY-MM-DD,YYYY-MM-DD,YYYY-MM-DD", tempUnitLines));
        }
        return tempObj;
    }


    async saveManualPlan(reqs: TempUnitModel[]): Promise<GlobalResponseObject> {

        // first validate if all the dates are valid
        for(const unit of reqs) {
            const dates = unit.dates?.split(",");
            if(dates.length == 0 || dates[0] == "") {
                throw new ErrorResponse(0,  `Dates not provided for the Unit : ${unit.unitId}`);
            }
            this.migHelperService.checkIfDatesValid(dates);
        }

        const linePlanEnts: LineDayPlanEntity[] = [];
        for(const unit of reqs) {
            const unitId = unit.unitId;
            const unitRec = await this.unitEnt.findOne({ where: { unitId: unitId }});
            if(!unitRec) {
                throw new ErrorResponse(0,  `Unit : ${unit.unitId} does not exist`);
            }
            const dates = unit.dates?.split(",");
            for(const date of dates) {
                if(!date) {
                    continue;
                }
                const unitConfiguredWorkingHours = await this.migHelperService.getUnitConfiguredWorkingHoursForDay(unitRec.unitId, date);
                const lastWorkingHour = unitConfiguredWorkingHours[unitConfiguredWorkingHours.length - 1];
                // get the per hour working minutes for the unit for a given date
                const perHourWorkingMinutes = await this.migHelperService.getUntiPlannedWorkingMinutesPerHoursForDay(unitRec.unitId, date);
                let totalMins = 0;
                perHourWorkingMinutes.forEach(hr => {
                    totalMins += hr;
                });
                
                unit.lines.forEach(line => {
                    let lineDayQty = line?.planQty > 0 ? line?.planQty : 0; // default to 1000 if no plan is saved
                    let perMinQty = 0;
                    if(lineDayQty > 0) {
                        perMinQty = Number((lineDayQty/totalMins).toFixed(3));
                    }
                    // let pendingQty = lineDayQty - (perMinQty * totalMins);
                    // console.log(pendingQty);
                    let consumedQty = 0;
                    unitConfiguredWorkingHours.forEach(hr => {
                        const currHourWorkingMins = perHourWorkingMinutes.get(hr);
                        const ent = new LineDayPlanEntity();
                        ent.planDate = date;
                        ent.unitId = unitRec.unitId;
                        ent.lineId = line.lineId;
                        ent.styleId = 'NOSTYLE';
                        if(lineDayQty > 0) {
                            ent.planQuantity = Math.floor(perMinQty * currHourWorkingMins);
                            // assign all the pending qty to the last hour
                            if(hr == lastWorkingHour) {
                                // ent.planQuantity = perMinQty * currHourWorkingMins + consumedQty;
                                ent.planQuantity = lineDayQty - consumedQty;
                            }
                            consumedQty += Math.floor(ent.planQuantity);
                        } else {
                            ent.planQuantity = 0;
                        }
                        ent.buyer = 'NO BUYER';
                        // ent.buyer = Number(rec.line_id) < 5 ? 'Reliance' : Number(rec.line_id) < 10 ? 'DNMX' : 'H&M';
                        ent.hour = hr;
                        linePlanEnts.push(ent);
                    });
                });
                // delete the whole plan for that sepcific day
                await this.linePlanEnt.delete({ unitId: unitRec.unitId, planDate: date});
            }
        }
        if(linePlanEnts.length > 0) {
            await this.linePlanEnt.save(linePlanEnts, {reload: false})
        }
        
        return new GlobalResponseObject(true, 0, 'Plan saved');
    }

    /**
     * saves the target efficiencies for the unit + line + date
     * @param lineEffReqs 
     * @returns 
     */
    async saveLineTargetEfficiency(lineEffReqs: LineTargetEffRequest[]): Promise<GlobalResponseObject> {
        // const transManager = new GenericTransactionManager();
        try {
            // await transManager.startTransaction();
            const activeUnits = await this.migHelperService.getAllUnits();
            const unitsSet = new Set<string>();
            const unitLinesMap = new Map<string, Set<string>>();

            activeUnits.forEach(r => unitsSet.add(r.unitId));
            for(const unit of unitsSet) {
                unitLinesMap.set(unit, new Set<string>());
                const linesInUnit = await this.lineEnt.find({select: ['lineId'], where: { unitId: unit }});
                linesInUnit.forEach(l => {
                    unitLinesMap.get(unit).add(l.lineId);
                });
            }
            // check if the unit is present
            lineEffReqs.forEach(r => {
                if(!unitLinesMap.has(r.unitId)) {
                    throw new ErrorResponse(0, `Unit ${r.unitId} does not exist`);
                }
                if(!unitLinesMap.get(r.unitId).has(r.lineId)) {
                    throw new ErrorResponse(0, `Line : ${r.lineId} in the unit : ${r.unitId} does not exist`);
                }
            });

            const lineTargetEffEnts: LineTargetEffEntity[] = [];
            for(const lineTargetEffReq of lineEffReqs) {
                // check if there is already an eff updated for the line + date. then update all those records isActive to false 
                await this.lineTargetEffEnt.update({ unitId: lineTargetEffReq.unitId, lineId: lineTargetEffReq.lineId, planDate: lineTargetEffReq.date}, {isActive: false});
                // now create a new record for the line + date + style
                const runningStyleInLine: {style_id: string}[] = await this.lineActualDayTrackEnt.createQueryBuilder('ladt')
                .select(`DISTINCT style_id`)
                .where(` unit_id = '${lineTargetEffReq.unitId}' AND plan_date = '${lineTargetEffReq.date}' AND line_id = '${lineTargetEffReq.lineId}' `)
                .getRawMany();
                if(runningStyleInLine.length > 0) {
                    runningStyleInLine.forEach(s => {
                        const lineTargetEffEnt = new LineTargetEffEntity();
                        lineTargetEffEnt.unitId = lineTargetEffReq.unitId;
                        lineTargetEffEnt.lineId = lineTargetEffReq.lineId;
                        lineTargetEffEnt.styleId = s.style_id;
                        lineTargetEffEnt.planDate = lineTargetEffReq.date;
                        lineTargetEffEnt.efficiency =  lineTargetEffReq.targetEff;
                        lineTargetEffEnts.push(lineTargetEffEnt);
                    });
                } else {
                    const lineTargetEffEnt = new LineTargetEffEntity();
                    lineTargetEffEnt.unitId = lineTargetEffReq.unitId;
                    lineTargetEffEnt.lineId = lineTargetEffReq.lineId;
                    lineTargetEffEnt.styleId = '0';
                    lineTargetEffEnt.planDate = lineTargetEffReq.date;
                    lineTargetEffEnt.efficiency =  lineTargetEffReq.targetEff;
                    lineTargetEffEnts.push(lineTargetEffEnt);
                }
            }
            await this.lineTargetEffEnt.save(lineTargetEffEnts, {reload: false});
            // await transManager.completeTransaction();
            return new GlobalResponseObject(true, 0, 'Eff saved successfully');
        } catch (error) {
            throw error;
        }
    }


    /**
     * saves the target efficiencies for the unit + line + date
     * @param lineEffReqs 
     * @returns 
     */
    async saveStyleTargetEfficiency(styleEffReqs: StyleTargetEffRequest): Promise<GlobalResponseObject> {
        try {
            for(const styleReq of styleEffReqs.styles) {
                // check if the style is present
                const styleRec = await this.styleEnt.findOne({ select: ['styleName'], where: { unitId: styleEffReqs.unitId, styleName: styleReq.styleName}});
                if(!styleRec) {
                    throw new ErrorResponse(0, `Style with name : ${styleReq.styleName} is not found`);
                }
                await this,this.styleEnt.update({ unitId: styleEffReqs.unitId, styleName: styleReq.styleName }, { targetEff: styleReq.targetEff });
            }
            return new GlobalResponseObject(true, 0, 'Eff saved successfully');
        } catch (error) {
            throw error;
        }
    }

}


















// const query = `SELECT
        //     hrd.HRD_HM_Key, hrd.HRD_OC_Key, hrd.HRD_EM_Key, hrd.HRD_Line, hrd.HRD_Workstation, COUNT(DISTINCT hrd.HRD_HM_Key)
        //     hm.HM_SRM_Key, hm.HM_ST_Key, hm.HM_STPO_Key, SUM(HRD_Started_Time - HRD_Completed_Time) as time_spent_in_secs
        //     FROM P_Hanger_Routing_Details hrd
        //     LEFT JOIN P_Hanger_Master hm ON hrd.HRD_HM_Key = hm.HM_Key
        //     WHERE HRD_Completed_Time >= '2023-04-21 00:00:00'
        //     AND HRD_ODP_Credited  = 1 AND HRD_OC_Completed = 1 
        //     AND HRD_Started_Time BETWEEN '2023-04-21 ${hour}:00:00' AND '2023-04-21 ${hour}:59:99'
        //     GROUP BY hrd.HRD_LINE, hrd.HRD_Workstation, hrd.HRD_EM_Key, hrd.HRD_OC_Key, 
        // 

        
//     SELECT 20 as LineId,  d.HRDH_Workstation, d.HRDH_EM_Key, d.HRDH_OC_Key, count(1) as completed_quantity, 
//                 DATEPART(hour, d.HRDH_Completed_Time) as hour, SUM(DATEDIFF(ss, d.HRDH_Started_Time, d.HRDH_Completed_Time)) as actual_time, 1 as ODPD_Is_Overtime
//                 FROM IHS_HISTORY.dbo.Hanger_Routing_Details_History d
//                 WHERE d.HRDH_Completed_Time BETWEEN '2023-05-06 08:00:00' AND '2023-05-06 19:00:00'
//                 --AND HRDH_ODP_Credited = 1 
//                 AND d.HRDH_OC_Completed = 1
//                 and HRDH_Workstation = 32
//                 GROUP BY d.HRDH_Workstation, d.HRDH_OC_Key, d.HRDH_EM_Key, DATEPART(hour, d.HRDH_Completed_Time)
//                 ORDER BY hour, HRDH_Workstation ASC
// 	925
//     1043            
                
// SELECT *
//  -- 20 as LineId,  d.HRD_Workstation, d.HRD_EM_Key, d.HRD_OC_Key, count(1) as completed_quantity, 
// -- DATEPART(hour, d.HRD_Completed_Time) as hour, 
// -- SUM(DATEDIFF(ss, d.HRD_Started_Time, d.HRD_Completed_Time)) as actual_time, 1 as ODPD_Is_Overtime
// FROM IHS.dbo.Hanger_Routing_Details d
// WHERE d.HRD_Completed_Time BETWEEN '2023-05-06 17:00:00' AND '2023-05-06 18:00:00'
// AND HRD_ODP_Credited = 1 AND d.HRD_OC_Completed = 1
// and HRD_Workstation = 32
// GROUP BY d.HRD_Workstation, d.HRD_OC_Key, d.HRD_EM_Key, DATEPART(hour, d.HRD_Completed_Time)
// ORDER BY hour, HRD_Workstation ASC